"use client";

import { createContext, useContext, ReactNode, useMemo, useEffect, useRef, useState, useCallback } from "react";
import { useQuery } from "convex/react";
import { api } from "@convex/_generated/api";
import { Doc } from "@convex/_generated/dataModel";
import { useWidgetSync } from "@/hooks/use-widget-sync";
import { useTimeAwareness } from "@/hooks/use-time-awareness";

const CACHE_KEY = "flextrack_dashboard_cache";
const CACHE_MAX_AGE_MS = 24 * 60 * 60 * 1000; // 24 hours max staleness

interface TodaySummary {
  blockCount: number;
  totalEarnings: number;
  totalMiles: number;
  totalMinutes: number;
  actualMinutes: number | null;
  avgHourlyRate: number;
  actualHourlyRate: number | null;
  avgPerMileRate: number;
}

interface RollingHours {
  hoursWorkedLast7Days: number;
  hoursAvailable: number;
  hoursWorkedToday: number;
  dailyLimit: number;
  hoursAvailableToday: number;
  hoursLeftToday: number;
  weeklyLimit: number;
  weeklyEarnings: number;
  isExtendedHours: boolean;
  hoursReleasingTomorrow: number;
  // New fields for true rolling
  bottleneck: "daily" | "weekly" | "none";
  nextReleaseTime: number | null;
  nextReleaseHours: number;
}

interface WeeklySnapshot {
  dailyEarnings: Array<{ day: string; earnings: number }>;
  totalEarnings: number;
  totalHours: number;
  hourlyRate: number;
  weeklyGoal: number | null;
  totalMiles: number;
  totalPackages: number;
  totalTips: number;
  blockCount: number;
  packagesPerHour: number;
  perMileRate: number;
}

interface DashboardState {
  activeShift: Doc<"shifts"> | null;
  activeBlock: Doc<"blocks"> | null;
  todaySummary: TodaySummary;
  rollingHours: RollingHours;
  weeklySnapshot: WeeklySnapshot;
  isLoading: boolean;
  isStale: boolean; // True when showing cached data, waiting for fresh
  showEndRouteReminder: boolean;
  dismissEndRouteReminder: () => void;
}

// Cache helpers
interface CachedData {
  timestamp: number;
  data: Omit<DashboardState, "isLoading" | "isStale" | "showEndRouteReminder" | "dismissEndRouteReminder">;
}

function loadFromCache(): Omit<DashboardState, "isLoading" | "isStale" | "showEndRouteReminder" | "dismissEndRouteReminder"> | null {
  if (typeof window === "undefined") return null;
  try {
    const cached = localStorage.getItem(CACHE_KEY);
    if (!cached) return null;

    const parsed: CachedData = JSON.parse(cached);
    const age = Date.now() - parsed.timestamp;

    // Don't use cache older than 24 hours
    if (age > CACHE_MAX_AGE_MS) {
      localStorage.removeItem(CACHE_KEY);
      return null;
    }

    return parsed.data;
  } catch {
    return null;
  }
}

function saveToCache(data: Omit<DashboardState, "isLoading" | "isStale" | "showEndRouteReminder" | "dismissEndRouteReminder">) {
  if (typeof window === "undefined") return;
  try {
    const cached: CachedData = {
      timestamp: Date.now(),
      data,
    };
    localStorage.setItem(CACHE_KEY, JSON.stringify(cached));
  } catch {
    // localStorage full or unavailable - ignore
  }
}

const emptyData = {
  activeShift: null,
  activeBlock: null,
  todaySummary: {
    blockCount: 0,
    totalEarnings: 0,
    totalMiles: 0,
    totalMinutes: 0,
    actualMinutes: null,
    avgHourlyRate: 0,
    actualHourlyRate: null,
    avgPerMileRate: 0,
  },
  rollingHours: {
    hoursWorkedLast7Days: 0,
    hoursAvailable: 0,
    hoursWorkedToday: 0,
    dailyLimit: 8,
    hoursAvailableToday: 0,
    hoursLeftToday: 0,
    weeklyLimit: 40,
    weeklyEarnings: 0,
    isExtendedHours: false,
    hoursReleasingTomorrow: 0,
    bottleneck: "none" as const,
    nextReleaseTime: null,
    nextReleaseHours: 0,
  },
  weeklySnapshot: {
    dailyEarnings: [
      { day: "Mon", earnings: 0 },
      { day: "Tue", earnings: 0 },
      { day: "Wed", earnings: 0 },
      { day: "Thu", earnings: 0 },
      { day: "Fri", earnings: 0 },
      { day: "Sat", earnings: 0 },
      { day: "Sun", earnings: 0 },
    ],
    totalEarnings: 0,
    totalHours: 0,
    hourlyRate: 0,
    weeklyGoal: null,
    totalMiles: 0,
    totalPackages: 0,
    totalTips: 0,
    blockCount: 0,
    packagesPerHour: 0,
    perMileRate: 0,
  },
};

const defaultState: DashboardState = {
  ...emptyData,
  isLoading: true,
  isStale: false,
  showEndRouteReminder: false,
  dismissEndRouteReminder: () => {},
};

const DashboardContext = createContext<DashboardState>(defaultState);

export function DashboardProvider({ children }: { children: ReactNode }) {
  // Use time awareness hook - updates at midnight and on app resume
  const { now } = useTimeAwareness();

  // Load cached data once on mount
  const cachedData = useRef<Omit<DashboardState, "isLoading" | "isStale" | "showEndRouteReminder" | "dismissEndRouteReminder"> | null>(null);
  if (cachedData.current === null) {
    cachedData.current = loadFromCache();
  }

  // Get local date/time based on current timestamp
  const localDateTime = useMemo(() => {
    const date = new Date(now);
    return {
      localDate: `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, "0")}-${date.getDate().toString().padStart(2, "0")}`,
      localTime: `${date.getHours().toString().padStart(2, "0")}:${date.getMinutes().toString().padStart(2, "0")}`,
    };
  }, [now]);

  // Pass timezone offset for correct block time interpretation
  // Server uses Date.now() for rolling window (always fresh), client provides timezone
  const timezoneOffset = useMemo(() => new Date().getTimezoneOffset(), []);
  const data = useQuery(api.dashboard.getDashboardState, {
    ...localDateTime,
    timezoneOffsetMinutes: timezoneOffset, // e.g., 480 for PST (UTC-8)
  });

  // Sync dashboard data to iOS widgets
  useWidgetSync();

  // Save to cache when we get fresh data
  useEffect(() => {
    if (data) {
      const dataToCache = {
        activeShift: data.activeShift,
        activeBlock: data.activeBlock,
        todaySummary: data.todaySummary,
        rollingHours: data.rollingHours,
        weeklySnapshot: data.weeklySnapshot,
      };
      saveToCache(dataToCache);
      cachedData.current = dataToCache;
    }
  }, [data]);

  // End route reminder state
  const [showEndRouteReminder, setShowEndRouteReminder] = useState(false);

  const dismissEndRouteReminder = useCallback(() => {
    setShowEndRouteReminder(false);
  }, []);

  // Timer to trigger end route reminder when block's shiftEnd time passes
  useEffect(() => {
    const activeShift = data?.activeShift;
    const activeBlock = data?.activeBlock;

    // Only trigger for active/paused shifts linked to a block
    if (!activeShift || activeShift.status === "completed" || !activeShift.blockId) {
      setShowEndRouteReminder(false);
      return;
    }

    if (!activeBlock) {
      return;
    }

    // Parse block end time (combine block.date + block.shiftEnd)
    const [hours, minutes] = activeBlock.shiftEnd.split(":").map(Number);
    const blockEndDate = new Date(activeBlock.date);
    blockEndDate.setHours(hours, minutes, 0, 0);
    const blockEndMs = blockEndDate.getTime();

    const now = Date.now();
    const delay = blockEndMs - now;

    // If already past end time, show immediately
    if (delay <= 0) {
      setShowEndRouteReminder(true);
      return;
    }

    // Set timeout for when block ends
    const timeout = setTimeout(() => {
      setShowEndRouteReminder(true);
    }, delay);

    return () => clearTimeout(timeout);
  }, [data?.activeShift, data?.activeBlock]);

  // Show cached data immediately, then fresh data when available
  const state = useMemo<DashboardState>(() => {
    // Fresh data available - use it
    if (data) {
      return {
        activeShift: data.activeShift,
        activeBlock: data.activeBlock,
        todaySummary: data.todaySummary,
        rollingHours: data.rollingHours,
        weeklySnapshot: data.weeklySnapshot,
        isLoading: false,
        isStale: false,
        showEndRouteReminder,
        dismissEndRouteReminder,
      };
    }

    // Still loading - show cached data if available
    if (data === undefined) {
      if (cachedData.current) {
        return {
          ...cachedData.current,
          isLoading: false, // Don't show loading skeleton
          isStale: true, // But mark as stale
          showEndRouteReminder,
          dismissEndRouteReminder,
        };
      }
      // No cache - show loading state
      return {
        ...defaultState,
        showEndRouteReminder,
        dismissEndRouteReminder,
      };
    }

    // data === null (user not authenticated)
    return {
      ...emptyData,
      isLoading: false,
      isStale: false,
      showEndRouteReminder,
      dismissEndRouteReminder,
    };
  }, [data, showEndRouteReminder, dismissEndRouteReminder]);

  return (
    <DashboardContext.Provider value={state}>
      {children}
    </DashboardContext.Provider>
  );
}

export function useDashboard() {
  return useContext(DashboardContext);
}
